// ─────────────────────────────────────────────
//  OnCallEditModal — set the on-call CRNA(s) for the room that must be covered.
//
//  One CRNA is the ordinary day; two is the split, each with their own window.
//
//  ⚠️ IT WRITES ASSIGNMENT ROWS, THROUGH THE EXISTING PAIR. `updateAssignmentRow`
//  where a row exists, `createAssignment` where it does not, and the room mirror
//  after — the same shape AssignmentModal.handleSave uses. No second write path.
//
//  ⚠️ A SPLIT THAT LEAVES A GAP IS REFUSED, IN WORDS. OB is the room the board
//  asserts must always be covered, and a hole written into it is invisible: the
//  coverage rule checks each on-call row on its own and both halves look fine.
//  See onCallWindows.coverageGap for why the check is circular (3 PM–7 AM is the
//  night shift, not an inverted range).
//
//  ⚠️ AND A SECOND ROW THAT IS NO LONGER WANTED IS DELETED. Left behind, the
//  board goes on believing somebody is covering the back half of the day who is
//  not — with the coverage rule itself reporting all-clear.
//
//  NO ALARM COLOURS: the gap warning is amber. Red and blinking on this board
//  mean an emergency, and spending that colour on a form error teaches staff to
//  ignore the one that matters.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import tokens from "../../theme/tokens";
import Modal from "../Modal";
import TimeCombo from "../TimeCombo";
import { hmToMinutes } from "../../domain/timeline";
import { roomForOrRoom } from "../../domain/gridTemplate";
import {
  createAssignment,
  updateAssignmentRow,
  deleteAssignmentRow,
  setRoomProvider,
} from "../../firebase/firestore";
import {
  FULL_DAY_START,
  FULL_DAY_END,
  ON_CALL_TIME_OPTIONS,
  isFullDay,
  validateOnCall,
  windowCovers,
} from "./onCallWindows";

const { color, font, radius } = tokens;

export default function OnCallEditModal({
  onClose,
  roomName,
  rooms = [],
  slotRows = [],
  providers = [],
  facilityId = "default",
  date = "",
  nowMin = null,
}) {
  const [mode, setMode] = useState(slotRows.filter((r) => r.ahpId).length > 1 ? "two" : "one");
  // ⚠️ THE SPLIT DEFAULTS CANNOT INHERIT A FULL-DAY WINDOW. Today's single
  // on-call row is 07:00→07:00 (the whole clock). Seeding the first half of a
  // split from it would leave that half covering all 24 hours: the gap check
  // would then find nothing wrong however the second CRNA's times were set, and
  // the "split" would be two people both booked for the entire day.
  const [slots, setSlots] = useState(() => {
    const r0 = slotRows[0] || null;
    const r1 = slotRows[1] || null;
    const start0 = r0?.startTime || FULL_DAY_START;
    const end0 = r0 && !isFullDay(r0) ? r0.endTime : "15:00";
    return [
      { ahpId: r0?.ahpId || "", startTime: start0, endTime: end0 },
      {
        ahpId: r1?.ahpId || "",
        startTime: r1?.startTime || end0,
        endTime: r1?.endTime || FULL_DAY_END,
      },
    ];
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const nameOf = (id) => providers.find((p) => p.id === id)?.name || "";
  const setSlot = (i, patch) =>
    setSlots((prev) => prev.map((v, idx) => (idx === i ? { ...v, ...patch } : v)));

  const withNames = slots.map((v) => ({ ...v, name: nameOf(v.ahpId) }));
  const blocked = validateOnCall({ mode, slots: withNames, roomName });

  const handleSave = async () => {
    if (blocked) return;
    setSaving(true);
    setSaveError(null);
    try {
      // The rows this save is going to produce, in order. ONE CRNA covers the
      // whole clock — the app's own on-call convention (07:00 → 07:00), stated
      // in the modal so the operator is not guessing what "one" means.
      const wanted = (mode === "two" ? withNames : [withNames[0]]).map((v, i) => ({
        ahpId: v.ahpId,
        ahpName: nameOf(v.ahpId),
        startTime: mode === "two" ? v.startTime : FULL_DAY_START,
        endTime: mode === "two" ? v.endTime : FULL_DAY_END,
        existing: slotRows[i] || null,
      }));

      for (const w of wanted) {
        const provider = providers.find((p) => p.id === w.ahpId) || null;
        const data = {
          facilityId: facilityId || "default",
          // The DATE ON SCREEN, not `rosterDateKey()`. AssignmentModal stamps
          // today because the board it opens from is always today; this page has
          // a free date picker, and stamping today from another day would write
          // onto the live board while the operator looked at a different one.
          date,
          orRoom: w.existing?.orRoom || roomName,
          isOnCall: true,
          ahpId: w.ahpId,
          ahpName: w.ahpName,
          ahpShiftCode: provider?.shiftCode ?? null,
          startTime: w.startTime,
          endTime: w.endTime,
          source: "manual",
        };
        // ⚠️ AN ACKNOWLEDGEMENT BELONGS TO THE PERSON IT WAS MADE ABOUT.
        // `coverageAckKey` is compared against an absence key ("room:WOR 3"),
        // never against a name — so leaving the previous CRNA's ack on a row
        // that now names somebody else could suppress a real "OB is not
        // covered" warning for the new person.
        if (w.existing && w.existing.ahpId !== w.ahpId) {
          data.coverageAckKey = null;
          data.coverageAckByName = null;
        }
        if (w.existing?.id) await updateAssignmentRow(w.existing.id, data);
        else await createAssignment(data);
      }

      // Two back to one: the leftover row must GO, not linger.
      for (const stale of slotRows.slice(wanted.length)) {
        if (stale?.id) await deleteAssignmentRow(stale.id);
      }

      // Mirror onto the live room doc, so `room.providerId` is not still naming
      // whoever was on call before this edit (firestore.js:334 — a room that
      // reads as staffed and is not is the worst failure this board has).
      //
      // ⚠️ IT NAMES WHOEVER IS ON AT THE MOMENT OF THE SAVE, and nothing
      // re-writes it at the handover — the timeline and this card read the rows
      // and stay right; the room doc is a snapshot. `setRoomProvider`, not
      // `setRoomAssignment`: this changes staffing, not the room's status, and
      // forcing "ready" onto a running room would re-label a live case.
      const room = roomForOrRoom(roomName, rooms);
      if (room?.id) {
        const onNow =
          wanted.find((w) =>
            windowCovers(
              { startMin: hmToMinutes(w.startTime), endMin: hmToMinutes(w.endTime) },
              nowMin
            )
          ) || wanted[0];
        const provider = providers.find((p) => p.id === onNow.ahpId) || null;
        await setRoomProvider(room.id, {
          providerId: onNow.ahpId || null,
          providerName: onNow.ahpName || "",
          providerRole: provider?.subRole ? provider.subRole.toUpperCase() : provider?.role || "",
        });
      }

      onClose();
    } catch (err) {
      console.error("[OnCallEditModal] save failed:", err);
      // Verbatim, not a generic sentence: a rules refusal is the operator's
      // answer, and a re-worded one is unactionable.
      setSaveError(err?.message || "The on-call change could not be saved.");
      setSaving(false);
    }
  };

  const crnaSelect = (i) => (
    <select
      style={st.select}
      aria-label={mode === "two" ? `CRNA ${i + 1}` : "On-call CRNA"}
      value={slots[i].ahpId}
      onChange={(e) => setSlot(i, { ahpId: e.target.value })}
    >
      <option value="">Pick a CRNA/AA…</option>
      {providers.map((p) => (
        <option key={p.id} value={p.id}>
          {p.name}
        </option>
      ))}
    </select>
  );

  // The rail is narrow but this modal is not — still, the times sit on their own
  // line under the name, the pattern the rail's cards use, so the two read the
  // same way.
  const timePair = (i, who) => (
    <div style={st.timeRow}>
      <TimeCombo
        ariaLabel={`${who} start time`}
        value={slots[i].startTime}
        options={ON_CALL_TIME_OPTIONS}
        onChange={(v) => setSlot(i, { startTime: v })}
      />
      <TimeCombo
        ariaLabel={`${who} end time`}
        value={slots[i].endTime}
        options={ON_CALL_TIME_OPTIONS}
        onChange={(v) => setSlot(i, { endTime: v })}
      />
    </div>
  );

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`CRNA on call — ${roomName || "no room"}`}
      maxWidth={420}
      footer={
        <>
          <button type="button" style={st.outlineBtn} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            style={st.btnPrimary}
            disabled={saving || Boolean(blocked)}
            onClick={handleSave}
          >
            {saving ? "Saving…" : "Save on call"}
          </button>
        </>
      }
    >
      <div style={st.modeRow} role="radiogroup" aria-label="How many CRNAs are on call">
        {[
          ["one", "One CRNA"],
          ["two", "Two — they split the day"],
        ].map(([k, labelText]) => (
          <button
            key={k}
            type="button"
            role="radio"
            aria-checked={mode === k}
            style={mode === k ? st.modeBtnOn : st.modeBtn}
            onClick={() => setMode(k)}
          >
            {labelText}
          </button>
        ))}
      </div>

      {/* ⚠️ A VISUAL CAPTION, NOT A FIELD LABEL. This heads a select AND a pair
          of time inputs, so <label> was a promise to one control that could
          never be kept. Every control under it already names itself with
          aria-label, so the caption carries no accessible name of its own —
          an earlier attempt gave the wrapper role="group" + aria-labelledby and
          produced "On-call CRNA group, On-call CRNA combo box", the same words
          twice. A duplicate name is noise, not information. */}
      <div style={st.group}>
        <div style={st.label}>{mode === "two" ? "First CRNA" : "On-call CRNA"}</div>
        {crnaSelect(0)}
        {mode === "two" && timePair(0, "First CRNA")}
      </div>

      {mode === "two" && (
        <div style={st.group}>
          <div style={st.label}>Second CRNA</div>
          {crnaSelect(1)}
          {timePair(1, "Second CRNA")}
        </div>
      )}

      {mode === "one" && (
        <p style={st.note}>
          One CRNA covers the whole 24 hours — 7 AM to 7 AM the next morning.
        </p>
      )}

      {/* The refusal, in words, beside the times that caused it. */}
      {blocked && (
        <p style={st.warn} role="status" data-testid="on-call-blocked">
          {blocked}
        </p>
      )}

      {/* ⚠️ THE LIMITATION, SAID OUT LOUD BESIDE THE CHOICE THAT CAUSES IT.
          `assignmentForRoom` (domain/gridTemplate.js:151) is `list.find(...)`,
          the FIRST match, and the Daily List grid and the room card both read
          through it. The timeline iterates assignments, so it shows both bars.
          An operator who learns this from the grid instead of from here will
          assume the save failed. Same wording pattern AssignmentModal uses for
          its "save as a new case" path — and NOT something to fix by editing
          `assignmentForRoom`'s callers from this card. */}
      {mode === "two" && (
        <p style={st.warn} data-testid="on-call-first-match">
          With two on-call CRNAs the timeline shows both, but the Daily List grid
          and the {roomName || "room"} card keep showing only the first.
        </p>
      )}

      {saveError && (
        <p style={st.error} role="alert">
          {saveError}
        </p>
      )}
    </Modal>
  );
}

const st = {
  modeRow: { display: "flex", gap: 8, marginBottom: 16 },
  // 44px minimum — this is pressed one-handed in a corridor.
  modeBtn: {
    flex: 1,
    minHeight: 44,
    padding: "8px 10px",
    fontSize: 12.5,
    fontFamily: "inherit",
    color: color.text.secondary,
    background: "transparent",
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.sm,
    cursor: "pointer",
  },
  modeBtnOn: {
    flex: 1,
    minHeight: 44,
    padding: "8px 10px",
    fontSize: 12.5,
    fontWeight: font.weight.bold,
    fontFamily: "inherit",
    color: "#ffffff",
    background: color.accent.primary,
    border: `1px solid ${color.accent.primary}`,
    borderRadius: radius.sm,
    cursor: "pointer",
  },
  group: { marginBottom: 16 },
  label: {
    display: "block",
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
    color: color.text.secondary,
    marginBottom: 6,
  },
  // ⚠️ 16px, NOT the 13 the desktop modals use. WKWebView auto-zooms any
  // focusable field under 16px and the zoom PERSISTS across tabs (see
  // pages/mobile/inputZoom.test.js). The rail this opens from stacks full-width
  // on a phone and is reachable with ?forceNative=1, so it is not desktop-only —
  // and the exemption list that guard carries is documented as DEBT, not a
  // licence to add to it.
  select: {
    width: "100%",
    background: color.surface.raised,
    border: `1px solid ${color.border.default}`,
    color: color.text.primary,
    borderRadius: radius.sm,
    padding: "9px 12px",
    fontSize: 16,
    boxSizing: "border-box",
  },
  timeRow: { display: "flex", gap: 10, marginTop: 8 },
  note: { margin: "0 0 8px", fontSize: 12.5, lineHeight: 1.4, color: color.text.muted },
  // Amber. Red on this board means an emergency (see the header note).
  warn: {
    margin: "8px 0 0",
    fontSize: 12.5,
    lineHeight: 1.4,
    color: "#7a4f00",
    background: "#fef8ec",
    border: "1px solid #f59e0b",
    borderRadius: radius.sm,
    padding: "7px 9px",
  },
  // A save that was REFUSED by the server is a different thing from a form the
  // client will not submit, so it gets the error ground and the AA-safe ink
  // (tokens: accent.error fails contrast as text).
  error: {
    margin: "8px 0 0",
    fontSize: 12.5,
    lineHeight: 1.4,
    color: color.surface.errorInk,
    background: color.surface.error,
    borderRadius: radius.sm,
    padding: "7px 9px",
  },
  outlineBtn: {
    background: "transparent",
    border: `1px solid ${color.border.default}`,
    color: color.text.secondary,
    borderRadius: radius.sm,
    padding: "10px 18px",
    minHeight: 44,
    fontSize: 13,
    cursor: "pointer",
  },
  btnPrimary: {
    background: color.accent.primary,
    color: "#fff",
    border: "none",
    borderRadius: radius.sm,
    padding: "10px 18px",
    minHeight: 44,
    fontSize: font.size.body,
    fontWeight: font.weight.bold,
    cursor: "pointer",
  },
};
